// N2 (2026-05-08, ALGORITHM_FLOW v0.49) — "재료" 탭 component.
//   본질 design = 2 layer 분리. inventory table read + 단순 schema.
//   옛 1795 line 영역 영역 모두 폐기 (= 14 필드 / 4 필터 / locationId tab / orphan badge / threshold endpoint
//   / replace-modal / image upload / category modal 영역 영역 모두 폐기).

import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  inventoryControllerCreateMaterial,
  inventoryControllerListItems,
  inventoryControllerListLocations,
  inventoryControllerListLocationMappings,
  inventoryControllerListMaterials,
  inventoryControllerRemoveMaterial,
  inventoryControllerAdjustMaterialStock,
  inventoryControllerUpdateMaterial,
  locationsControllerList,
  locationsControllerUpdate,
} from '@dvvb/sdk';

import {
  AlertBanner,
  Button,
  FormField,
  ItemThumb,
  Modal,
  TextInput,
  useToast,
} from '../../components/primitives';
import { ApiCallError, unwrap } from '../../lib/envelope';
import { useAuth } from '../../auth/AuthContext';
import { API_BASE, getStoredToken } from '../../lib/api-client';
import { useViewport } from '../../lib/useViewport';
import type { InventoryItemRow, InventoryMaterialRow, LocationRow } from '../../types/api';
import { calculateMaterialShortage, stockShortageIssueGroup } from './auto-order-data';

interface ListEnvelope<T> {
  items: T[];
  count: number;
  limit: number;
  cursor: string | null;
  has_more: boolean;
}

interface LocationMappingRow {
  id: string;
  name: string;
  boxheroLocationId: string | null;
  boxheroMaterialCount: number;
}

interface LinkMaterialDraft {
  row: InventoryItemRow;
  unit: string;
  providerUnit: string;
  conversionRatio: number;
  minStock?: number;
  targetStock?: number;
  expiryDate?: string;
}

const OPERATING_UNITS = ['g', 'ml', 'ea'] as const;

type MaterialIssueGroup =
  | 'expired'
  | 'expiry-soon'
  | 'no-stock'
  | 'below-safety'
  | 'below-target'
  | 'conversion'
  | 'prep';
type MaterialIssueRoute = 'material' | 'auto-order' | 'work-schedule';
type MaterialIssueSeverity = 'danger' | 'warning' | 'info';

interface MaterialIssue {
  id: string;
  row: InventoryMaterialRow;
  group: MaterialIssueGroup;
  title: string;
  detail: string;
  route: MaterialIssueRoute;
  routeLabel: string;
  severity: MaterialIssueSeverity;
}

interface MaterialIssueBundle {
  row: InventoryMaterialRow;
  issues: MaterialIssue[];
}

const ISSUE_FILTERS: Array<{ id: 'all' | MaterialIssueGroup; label: string }> = [
  { id: 'all', label: '전체' },
  { id: 'expired', label: '만료' },
  { id: 'expiry-soon', label: '임박' },
  { id: 'no-stock', label: '재고 없음' },
  { id: 'below-safety', label: '안전 미만' },
  { id: 'below-target', label: '적정 미만' },
  { id: 'conversion', label: '환산' },
  { id: 'prep', label: '전처리/확인' },
];

const scrollTableWrapStyle: CSSProperties = {
  overflowX: 'auto',
  maxWidth: '100%',
  minWidth: 0,
  WebkitOverflowScrolling: 'touch',
};

function extractUnit(attrs: InventoryItemRow['attrs']): string {
  const candidates = ['unit', 'unit_name', 'uom', '단위'];
  if (!attrs) return '';
  if (Array.isArray(attrs)) {
    const lower = candidates.map((c) => c.toLowerCase());
    for (const entry of attrs) {
      if (!entry || typeof entry !== 'object') continue;
      const e = entry as Record<string, unknown>;
      const keyish = String(e.name ?? e.key ?? e.attr_name ?? '').toLowerCase();
      if (lower.includes(keyish)) {
        const v = e.value ?? e.val;
        if (typeof v === 'string' && v.trim()) return v.trim();
      }
    }
    return '';
  }
  for (const k of candidates) {
    const v = (attrs as Record<string, unknown>)[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function normalizeMaterialName(value: string | null | undefined): string {
  return (value ?? '').trim();
}

function hasOrderConversion(row: InventoryMaterialRow): boolean {
  return !!row.providerUnit && row.conversionRatio != null && row.conversionRatio > 0;
}

function conversionLabel(row: InventoryMaterialRow): string {
  if (row.providerKind === 'boxhero') return '역환산';
  if (hasOrderConversion(row)) return '발주 기준';
  return '우리 기준';
}

function formatQuantity(value: number): string {
  return value.toLocaleString('ko-KR', { maximumFractionDigits: 4 });
}

function formatComparableQuantity(row: InventoryMaterialRow): string {
  const unit = row.comparableUnit || row.unit;
  if (row.comparableQuantity == null) return '—';
  if (hasOrderConversion(row) && row.stock > 0 && row.comparableQuantity <= 0) {
    return `< 1 ${unit}`;
  }
  return `${formatQuantity(row.comparableQuantity)} ${unit}`;
}

function conversionDetail(row: InventoryMaterialRow): string {
  const label = conversionLabel(row);
  if (!hasOrderConversion(row) || row.orderEquivalentQuantity == null) return label;
  return `${label} · 실환산 ${formatQuantity(row.orderEquivalentQuantity)} ${row.comparableUnit || row.unit}`;
}

async function listAllMaterialsForLocation(locationId: string): Promise<InventoryMaterialRow[]> {
  const materials: InventoryMaterialRow[] = [];
  let cursor: string | null = null;

  for (let page = 0; page < 100; page += 1) {
    const response: Parameters<typeof unwrap<ListEnvelope<InventoryMaterialRow>>>[0] =
      await inventoryControllerListMaterials({
        query: {
          limit: '100',
          locationId,
          ...(cursor ? { cursor } : {}),
        } as never,
      });
    const envelope: ListEnvelope<InventoryMaterialRow> =
      unwrap<ListEnvelope<InventoryMaterialRow>>(response);
    materials.push(...envelope.items);
    if (!envelope.has_more || !envelope.cursor) break;
    cursor = envelope.cursor;
  }

  return materials;
}

function daysUntil(date: string | null): number | null {
  if (!date) return null;
  const [year, month, day] = date.split('-').map(Number);
  if (!year || !month || !day) return null;
  const expiry = new Date(year, month - 1, day);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.floor((expiry.getTime() - today.getTime()) / 86_400_000);
}

function buildMaterialIssues(items: InventoryMaterialRow[]): MaterialIssue[] {
  const issues: MaterialIssue[] = [];
  for (const row of items) {
    const expiryDays = daysUntil(row.expiryDate);
    const comparableUnit = row.comparableUnit || row.unit;
    const shortage = calculateMaterialShortage(row.comparableQuantity, row.minStock, row.targetStock);
    const stockGroup = stockShortageIssueGroup(row, shortage);
    if (expiryDays != null && expiryDays < 0) {
      issues.push({
        id: `${row.id}:expired`,
        row,
        group: 'expired',
        title: '유통기한 만료',
        detail: `${Math.abs(expiryDays)}일 지남. 폐기/재손질/재고조정 확인 필요`,
        route: 'work-schedule',
        routeLabel: '직원 할일 후보',
        severity: 'danger',
      });
    } else if (expiryDays != null && expiryDays <= 1) {
      issues.push({
        id: `${row.id}:expiry-soon`,
        row,
        group: 'expiry-soon',
        title: '유통기한 임박',
        detail: expiryDays === 0 ? '오늘까지 사용 가능' : `${expiryDays}일 남음`,
        route: 'material',
        routeLabel: '재료 확인',
        severity: 'warning',
      });
    }

    if (stockGroup === 'no-stock') {
      const stockDetail = `우리 현재고 ${row.stock.toLocaleString()} ${row.unit}`;
      issues.push({
        id: `${row.id}:no-stock`,
        row,
        group: 'no-stock',
        title: '재고 없음',
        detail:
          shortage > 0
            ? `${shortage.toLocaleString()} ${comparableUnit} 발주 필요 · ${stockDetail}`
            : stockDetail,
        route: 'auto-order',
        routeLabel: '자동발주 후보',
        severity: 'danger',
      });
    } else if (stockGroup === 'below-safety') {
      issues.push({
        id: `${row.id}:below-safety`,
        row,
        group: 'below-safety',
        title: '안전수량 이하',
        detail: `${shortage.toLocaleString()} ${comparableUnit} 발주 필요`,
        route: 'auto-order',
        routeLabel: '자동발주 후보',
        severity: 'danger',
      });
    } else if (stockGroup === 'below-target') {
      // 안전수량 이하가 아니지만 목표까지 부족 (예: 안전0·목표5·C2) — 기존 below-target(적정 미만) 분류.
      issues.push({
        id: `${row.id}:below-target`,
        row,
        group: 'below-target',
        title: '적정수량 미만',
        detail: `${shortage.toLocaleString()} ${comparableUnit} 발주 필요`,
        route: 'auto-order',
        routeLabel: '자동발주 후보',
        severity: 'warning',
      });
    }

    if (row.providerKind === 'boxhero') {
      if (row.providerQuantity === 0 && (row.minStock > 0 || shortage > 0)) {
        issues.push({
          id: `${row.id}:provider-zero`,
          row,
          group: 'no-stock',
          title: 'BoxHero 외부 수량 0',
          detail: '외부 재고 기준도 부족 신호',
          route: 'auto-order',
          routeLabel: '자동발주 후보',
          severity: 'danger',
        });
      }
      const diff = row.stockDifference;
      const converted = row.convertedProviderQuantity;
      if (row.conversionRatio == null || row.conversionRatio <= 0) {
        issues.push({
          id: `${row.id}:conversion-missing`,
          row,
          group: 'conversion',
          title: '환산 확인 필요',
          detail: '환산비율이 없어 자동 판단 불가',
          route: 'material',
          routeLabel: '재료 확인',
          severity: 'warning',
        });
      } else if (
        diff != null &&
        converted != null &&
        Math.abs(diff) > Math.max(1, Math.abs(converted) * 0.2)
      ) {
        issues.push({
          id: `${row.id}:conversion-drift`,
          row,
          group: 'conversion',
          title: '환산 차이 확인',
          detail: `우리 재고와 외부 환산 재고 차이 ${formatDifference(diff, row.unit)}`,
          route: 'material',
          routeLabel: '재료 확인',
          severity: 'info',
        });
      }
    } else if (!row.providerUnit || row.conversionRatio == null || row.conversionRatio <= 0) {
      issues.push({
        id: `${row.id}:order-conversion-missing`,
        row,
        group: 'conversion',
        title: '발주 단위 확인 필요',
        detail: '발주 단위와 환산비가 있어야 발주 기준 수량으로 계산됩니다',
        route: 'material',
        routeLabel: '재료 확인',
        severity: 'warning',
      });
    }
  }
  return issues;
}

function groupMaterialIssues(issues: MaterialIssue[]): MaterialIssueBundle[] {
  const grouped = new Map<string, MaterialIssueBundle>();
  for (const issue of issues) {
    const existing = grouped.get(issue.row.id);
    if (existing) {
      existing.issues.push(issue);
    } else {
      grouped.set(issue.row.id, { row: issue.row, issues: [issue] });
    }
  }
  return Array.from(grouped.values());
}

function uniqueIssueValues<T>(issues: MaterialIssue[], pick: (issue: MaterialIssue) => T): T[] {
  return Array.from(new Set(issues.map(pick)));
}

function issueSeverityColor(severity: MaterialIssueSeverity): string {
  if (severity === 'danger') return 'var(--danger)';
  if (severity === 'warning') return 'var(--warning)';
  return 'var(--fg-2)';
}

export function MaterialsTab() {
  const auth = useAuth();
  const toast = useToast();
  const isOwner = auth.role === 'owner';
  const qc = useQueryClient();
  const viewport = useViewport();
  const isMobile = viewport === 'mobile';
  const [q, setQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showMapping, setShowMapping] = useState(false);
  const [editing, setEditing] = useState<InventoryMaterialRow | null>(null);
  const [receiving, setReceiving] = useState<InventoryMaterialRow | null>(null);
  const [linkingCandidate, setLinkingCandidate] = useState<InventoryItemRow | null>(null);
  const [bulkExpiryDate, setBulkExpiryDate] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [issueFilter, setIssueFilter] = useState<'all' | MaterialIssueGroup>('all');

  const locationsQ = useQuery({
    queryKey: ['locations', 'inventory-materials'],
    queryFn: async () => {
      const r = await locationsControllerList({
        query: { limit: '100', status: 'active' } as never,
      });
      return unwrap<ListEnvelope<LocationRow>>(r);
    },
    staleTime: 30_000,
  });

  const locations = locationsQ.data?.items ?? [];

  useEffect(() => {
    const first = locations[0];
    if (!selectedLocationId && first) {
      setSelectedLocationId(first.id);
    }
  }, [locations, selectedLocationId]);

  const selectedLocation =
    locations.find((l) => l.id === selectedLocationId) ?? locations[0] ?? null;

  const boxheroLocationsQ = useQuery({
    queryKey: ['inventory', 'boxhero-locations-for-materials'],
    queryFn: async () => {
      const r = await inventoryControllerListLocations();
      return unwrap<{ items: Array<{ id: string; boxheroLocationId: string; name: string }> }>(r);
    },
    staleTime: 30_000,
  });

  const boxheroLocations = boxheroLocationsQ.data?.items ?? [];

  const mappingsQ = useQuery({
    queryKey: ['inventory', 'location-mappings'],
    queryFn: async () => {
      const r = await inventoryControllerListLocationMappings();
      return unwrap<{ items: LocationMappingRow[]; count: number }>(r);
    },
    staleTime: 30_000,
  });
  const locationMappings = mappingsQ.data?.items ?? [];
  const selectedMapping = selectedLocation
    ? locationMappings.find((loc) => loc.id === selectedLocation.id)
    : null;
  const currentBoxheroLocationId =
    selectedMapping?.boxheroLocationId ?? selectedLocation?.boxheroLocationId ?? '';
  const selectedBoxheroLocation =
    boxheroLocations.find((loc) => loc.boxheroLocationId === currentBoxheroLocationId) ?? null;
  const locationLinkText =
    currentBoxheroLocationId && selectedBoxheroLocation
      ? `${selectedLocation?.name ?? '사업장'} ↔ ${selectedBoxheroLocation.name}`
      : currentBoxheroLocationId
        ? `${selectedLocation?.name ?? '사업장'} ↔ BoxHero ID ${currentBoxheroLocationId}`
        : '미연동';

  const listQ = useQuery({
    queryKey: ['inventory', 'materials', { q, locationId: selectedLocation?.id ?? null }],
    queryFn: async () => {
      const r = await inventoryControllerListMaterials({
        query: {
          limit: '200',
          ...(q ? { q } : {}),
          ...(selectedLocation ? { locationId: selectedLocation.id } : {}),
        } as never,
      });
      return unwrap<ListEnvelope<InventoryMaterialRow>>(r);
    },
    enabled: !!selectedLocation,
    staleTime: 30_000,
    refetchOnMount: 'always',
  });

  const items = listQ.data?.items ?? [];
  const materialIssues = useMemo(() => buildMaterialIssues(items), [items]);

  const registeredMaterialsQ = useQuery({
    queryKey: [
      'inventory',
      'materials',
      'registered-index',
      { locationId: selectedLocation?.id ?? null },
    ],
    queryFn: async () => listAllMaterialsForLocation(selectedLocation!.id),
    enabled: !!selectedLocation,
    staleTime: 30_000,
    refetchOnMount: 'always',
  });

  const registeredMaterials = registeredMaterialsQ.data ?? [];
  const registeredProviderIds = useMemo(
    () =>
      new Set(
        registeredMaterials
          .map((m) => m.providerItemId)
          .filter((id): id is string => typeof id === 'string' && id.length > 0),
      ),
    [registeredMaterials],
  );
  const registeredMaterialNames = useMemo(
    () =>
      new Set(
        registeredMaterials
          .map((m) => normalizeMaterialName(m.name))
          .filter((name) => name.length > 0),
      ),
    [registeredMaterials],
  );

  const candidatesQ = useQuery({
    queryKey: [
      'inventory',
      'material-candidates',
      { boxheroLocationId: currentBoxheroLocationId || null },
    ],
    queryFn: async () => {
      const r = await inventoryControllerListItems({
        query: {
          limit: '500',
          locationId: currentBoxheroLocationId,
        } as never,
      });
      return unwrap<ListEnvelope<InventoryItemRow>>(r);
    },
    enabled: !!currentBoxheroLocationId,
    staleTime: 30_000,
  });

  const candidateItems = useMemo(
    () =>
      (candidatesQ.data?.items ?? []).filter(
        (row) =>
          !registeredProviderIds.has(row.boxheroItemId) &&
          !registeredMaterialNames.has(normalizeMaterialName(row.name)),
      ),
    [candidatesQ.data?.items, registeredMaterialNames, registeredProviderIds],
  );
  const materialCandidatesLoading = candidatesQ.isLoading || registeredMaterialsQ.isLoading;
  const showBulkLinkControls =
    isOwner && !!currentBoxheroLocationId && !materialCandidatesLoading && candidateItems.length > 0;
  const boxheroLinkedMaterials = useMemo(
    () =>
      registeredMaterials.filter(
        (row) => row.providerKind === 'boxhero' && typeof row.providerItemId === 'string',
      ),
    [registeredMaterials],
  );
  const showBulkUnlinkControls =
    isOwner && !registeredMaterialsQ.isLoading && boxheroLinkedMaterials.length > 0;

  const removeMu = useMutation({
    mutationFn: async (id: string) => {
      const r = await inventoryControllerRemoveMaterial({ path: { id } });
      return unwrap(r);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['inventory', 'materials'] });
      await qc.invalidateQueries({ queryKey: ['inventory', 'material-candidates'] });
      await qc.invalidateQueries({ queryKey: ['inventory', 'items'] });
      toast.success('재료를 삭제했습니다.');
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '삭제 실패');
    },
  });

  const bulkUnlinkMu = useMutation({
    mutationFn: async (rows: InventoryMaterialRow[]) => {
      const failed: string[] = [];
      let removed = 0;
      for (const row of rows) {
        try {
          const r = await inventoryControllerRemoveMaterial({ path: { id: row.id } });
          unwrap(r);
          removed += 1;
        } catch {
          failed.push(row.name);
        }
      }
      return { removed, failed };
    },
    onSuccess: async ({ removed, failed }) => {
      await qc.invalidateQueries({ queryKey: ['inventory', 'materials'] });
      await qc.invalidateQueries({ queryKey: ['inventory', 'material-candidates'] });
      await qc.invalidateQueries({ queryKey: ['inventory', 'items'] });
      if (failed.length > 0) {
        toast.error(`BoxHero 연동 ${removed}개 해지, ${failed.length}개 실패`);
        return;
      }
      toast.success(`BoxHero 연동 재료 ${removed}개를 해지했습니다.`);
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '일괄 해지 실패');
    },
  });

  const quickUpdateMu = useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: Record<string, unknown>;
    }) => {
      const r = await inventoryControllerUpdateMaterial({
        path: { id },
        body: body as never,
      });
      return unwrap(r);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['inventory', 'materials'] });
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '빠른 수정 실패');
    },
  });

  const stockAdjustMu = useMutation({
    mutationFn: async ({
      id,
      qty,
      note,
      expiryDate,
    }: {
      id: string;
      qty: number;
      note?: string;
      expiryDate?: string;
    }) => {
      let r = await inventoryControllerAdjustMaterialStock({
        path: { id },
        body: {
          qty,
          reason: qty > 0 ? 'manual_in' : 'manual_adjust',
          note,
        } as never,
      });
      if (expiryDate !== undefined) {
        r = await inventoryControllerUpdateMaterial({
          path: { id },
          body: { expiryDate } as never,
        });
      }
      return unwrap(r);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['inventory', 'materials'] });
      toast.success('재료 재고를 반영했습니다.');
      setReceiving(null);
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '재고 반영 실패');
    },
  });

  const importMu = useMutation({
    mutationFn: async (draft: LinkMaterialDraft) => {
      if (!selectedLocation) throw new Error('사업장을 먼저 선택하세요.');
      const row = draft.row;
      const r = await inventoryControllerCreateMaterial({
        body: {
          locationId: selectedLocation.id,
          name: row.name,
          unit: draft.unit,
          stock: row.quantity * draft.conversionRatio,
          cost: row.cost,
          minStock: draft.minStock,
          targetStock: draft.targetStock,
          providerKind: 'boxhero',
          providerItemId: row.boxheroItemId,
          imageUrl: row.photoUrl ?? undefined,
          providerUnit: draft.providerUnit || undefined,
          conversionRatio: draft.conversionRatio,
          expiryDate: draft.expiryDate || undefined,
        } as never,
      });
      return unwrap(r);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['inventory', 'materials'] });
      await qc.invalidateQueries({ queryKey: ['inventory', 'material-candidates'] });
      await qc.invalidateQueries({ queryKey: ['inventory', 'items'] });
      toast.success('BoxHero 품목을 우리 재료와 연동했습니다.');
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '등록 실패');
    },
  });

  const importAll = async () => {
    for (const row of candidateItems) {
      const providerUnit = extractUnit(row.attrs) || 'ea';
      await importMu.mutateAsync({
        row,
        unit: OPERATING_UNITS.includes(providerUnit as (typeof OPERATING_UNITS)[number])
          ? providerUnit
          : 'ea',
        providerUnit,
        conversionRatio: 1,
        expiryDate: bulkExpiryDate || undefined,
      });
    }
  };

  const unlinkAll = () => {
    if (boxheroLinkedMaterials.length === 0) return;
    const message =
      `현재 사업장의 BoxHero 연동 재료 ${boxheroLinkedMaterials.length}개를 일괄 해지할까요?\n` +
      '해지된 재료는 삭제 처리되고 BoxHero 후보로 다시 표시됩니다.';
    if (!window.confirm(message)) return;
    bulkUnlinkMu.mutate(boxheroLinkedMaterials);
  };

  if (locationsQ.isLoading || (listQ.isLoading && selectedLocation)) {
    return (
      <div style={{ padding: 24, color: 'var(--fg-3)', fontSize: 13 }}>
        재료 목록을 불러오는 중…
      </div>
    );
  }
  if (locationsQ.isError || listQ.isError) {
    return (
      <AlertBanner
        tone="danger"
        message={`재료 목록을 불러오지 못했습니다: ${
          ((locationsQ.error ?? listQ.error) as ApiCallError).message ?? '알 수 없는 오류'
        }`}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
      <div
        style={{
          padding: isMobile ? 12 : 16,
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-base)',
          borderRadius: 8,
          minWidth: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12,
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>
              우리 재료 · {selectedLocation?.name ?? '사업장'} ({items.length}개)
            </div>
            <div style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 3 }}>
              우리 사업장 기준 운영 재료입니다.
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              flexWrap: 'wrap',
              minWidth: 0,
              width: isMobile ? '100%' : 'auto',
            }}
          >
            <select
              value={selectedLocation?.id ?? ''}
              onChange={(e) => setSelectedLocationId(e.target.value || null)}
              style={{
                width: isMobile ? '100%' : 'auto',
                minWidth: 0,
                minHeight: 'var(--control-min-h)',
                boxSizing: 'border-box',
                padding: '7px 10px',
                border: '1px solid var(--border-base)',
                borderRadius: 6,
                fontSize: 13,
                background: 'var(--bg-surface)',
                color: 'var(--fg-1)',
              }}
            >
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </select>
            <input
              type="search"
              placeholder="재료 검색"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{
                flex: isMobile ? '1 1 100%' : '0 1 220px',
                minWidth: 0,
                minHeight: 'var(--control-min-h)',
                boxSizing: 'border-box',
                padding: '7px 12px',
                border: '1px solid var(--border-base)',
                borderRadius: 6,
                fontSize: 13,
                width: isMobile ? '100%' : 220,
              }}
            />
            {isOwner && (
              <Button
                variant="strong"
                onClick={() => setShowCreate(true)}
                disabled={!selectedLocation}
              >
                + 자체 등록
              </Button>
            )}
          </div>
        </div>
        <MaterialIssuePanel
          issues={materialIssues}
          filter={issueFilter}
          onFilterChange={setIssueFilter}
          isOwner={isOwner}
          onReceive={setReceiving}
        />
        <MaterialTable
          items={items}
          isOwner={isOwner}
          quickUpdatePending={quickUpdateMu.isPending}
          onQuickUpdate={(id, body) => quickUpdateMu.mutate({ id, body })}
          onReceive={setReceiving}
          onEdit={setEditing}
          onRemove={(row) => {
            if (window.confirm(`재료 "${row.name}" 을 삭제할까요?`)) {
              removeMu.mutate(row.id);
            }
          }}
        />
        <div
          style={{
            borderTop: '1px solid var(--border-weak)',
            borderBottom: '1px solid var(--border-weak)',
            padding: '12px 0',
            marginTop: 14,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              flexWrap: 'wrap',
              marginBottom: 10,
            }}
          >
            <div>
              <div style={{ fontSize: 13, fontWeight: 700 }}>BoxHero 외부 재고 연동</div>
              <div style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 3 }}>
                {locationLinkText}
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                flexWrap: 'wrap',
                minWidth: 0,
              }}
            >
              {showBulkLinkControls && (
                <input
                  type="date"
                  value={bulkExpiryDate}
                  onChange={(e) => setBulkExpiryDate(e.target.value)}
                  aria-label="일괄 연동 유통기한"
                  style={{
                    minHeight: 'var(--control-min-h)',
                    boxSizing: 'border-box',
                    padding: '7px 10px',
                    border: '1px solid var(--border-base)',
                    borderRadius: 6,
                    fontSize: 13,
                    background: 'var(--bg-surface)',
                    color: 'var(--fg-1)',
                  }}
                />
              )}
              {isOwner && (
                <Button
                  variant="strong"
                  onClick={() => setShowMapping(true)}
                  disabled={boxheroLocationsQ.isLoading || mappingsQ.isLoading}
                >
                  BoxHero 외부 재고 연동
                </Button>
              )}
              {showBulkLinkControls && (
                <Button
                  variant="secondary"
                  onClick={() => void importAll()}
                  disabled={importMu.isPending}
                >
                  일괄 연동 ({candidateItems.length})
                </Button>
              )}
              {showBulkUnlinkControls && (
                <Button
                  variant="ghost"
                  onClick={unlinkAll}
                  disabled={bulkUnlinkMu.isPending}
                  style={{ color: 'var(--danger)' }}
                >
                  일괄 해지 ({boxheroLinkedMaterials.length})
                </Button>
              )}
            </div>
          </div>
          {!selectedLocation ? (
            <div style={{ fontSize: 13, color: 'var(--fg-3)' }}>사업장을 먼저 선택하세요.</div>
          ) : !currentBoxheroLocationId ? (
            <div style={{ fontSize: 13, color: 'var(--fg-3)' }}>
              BoxHero 외부 재고 연동 버튼에서 이 사업장과 BoxHero 위치를 연결하세요.
            </div>
          ) : materialCandidatesLoading ? (
            <div style={{ fontSize: 13, color: 'var(--fg-3)' }}>BoxHero 품목을 불러오는 중…</div>
          ) : candidateItems.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--fg-3)' }}>
              이 사업장에서 새로 가져올 BoxHero 품목이 없습니다.
            </div>
          ) : (
            <CandidateTable
              rows={candidateItems}
              isOwner={isOwner}
              bulkExpiryDate={bulkExpiryDate}
              locationLinkText={locationLinkText}
              importPending={importMu.isPending}
              onLink={setLinkingCandidate}
            />
          )}
        </div>
      </div>
      {showCreate && isOwner && (
        <CreateMaterialModal
          location={selectedLocation}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: ['inventory', 'materials'] });
          }}
        />
      )}
      {editing && isOwner && (
        <EditMaterialModal
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ['inventory', 'materials'] });
          }}
        />
      )}
      {receiving && isOwner && (
        <ReceiveMaterialModal
          row={receiving}
          isPending={stockAdjustMu.isPending}
          onClose={() => setReceiving(null)}
          onSubmit={(qty, note, expiryDate) =>
            stockAdjustMu.mutate({ id: receiving.id, qty, note, expiryDate })
          }
        />
      )}
      {linkingCandidate && isOwner && (
        <LinkBoxheroMaterialModal
          row={linkingCandidate}
          location={selectedLocation}
          isPending={importMu.isPending}
          onClose={() => setLinkingCandidate(null)}
          onSubmit={(draft) => {
            importMu.mutate(draft, {
              onSuccess: () => setLinkingCandidate(null),
            });
          }}
        />
      )}
      {showMapping && isOwner && (
        <BoxheroMappingModal
          mappings={locationMappings}
          boxheroLocations={boxheroLocations}
          selectedLocationId={selectedLocation?.id ?? null}
          onClose={() => setShowMapping(false)}
          onChanged={async () => {
            await qc.invalidateQueries({ queryKey: ['locations', 'inventory-materials'] });
            await qc.invalidateQueries({ queryKey: ['inventory', 'location-mappings'] });
            await qc.invalidateQueries({ queryKey: ['inventory', 'material-candidates'] });
            await qc.invalidateQueries({ queryKey: ['inventory', 'materials'] });
          }}
        />
      )}
    </div>
  );
}

const th: React.CSSProperties = {
  padding: '8px 8px',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--fg-3)',
};

const td: React.CSSProperties = {
  padding: '8px 8px',
  fontSize: 13,
  color: 'var(--fg-1)',
};

function MaterialIssuePanel({
  issues,
  filter,
  onFilterChange,
  isOwner,
  onReceive,
}: {
  issues: MaterialIssue[];
  filter: 'all' | MaterialIssueGroup;
  onFilterChange: (filter: 'all' | MaterialIssueGroup) => void;
  isOwner: boolean;
  onReceive: (row: InventoryMaterialRow) => void;
}) {
  const counts = new Map<string, Set<string>>();
  for (const issue of issues) {
    const set = counts.get(issue.group) ?? new Set<string>();
    set.add(issue.row.id);
    counts.set(issue.group, set);
  }
  const visibleIssues = filter === 'all' ? issues : issues.filter((issue) => issue.group === filter);
  const issueBundles = groupMaterialIssues(visibleIssues);
  const total = groupMaterialIssues(issues).length;

  return (
    <div
      style={{
        border: '1px solid var(--border-base)',
        borderRadius: 8,
        background: 'var(--bg-subtle)',
        marginBottom: 14,
        overflow: 'hidden',
        minWidth: 0,
      }}
    >
      <div
        style={{
          padding: '12px 14px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          borderBottom: '1px solid var(--border-weak)',
        }}
      >
        <div>
          <div style={{ fontSize: 13, fontWeight: 800 }}>처리 필요 재료</div>
          <div style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 2 }}>
            만료·부족·환산 확인을 위에 모아서 놓습니다. 자동발주/작업일정은 후속 이벤트로 넘깁니다.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', minWidth: 0 }}>
          {ISSUE_FILTERS.map((item) => {
            const count = item.id === 'all' ? total : counts.get(item.id)?.size ?? 0;
            const active = filter === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onFilterChange(item.id)}
                style={{
                  border: `1px solid ${active ? 'var(--accent)' : 'var(--border-base)'}`,
                  background: active
                    ? 'color-mix(in srgb, var(--accent) 8%, var(--bg-surface))'
                    : 'var(--bg-surface)',
                  color: active ? 'var(--accent)' : 'var(--fg-2)',
                  borderRadius: 999,
                  padding: '5px 9px',
                  fontSize: 12,
                  fontWeight: active ? 700 : 500,
                  cursor: 'pointer',
                }}
              >
                {item.label} {count}
              </button>
            );
          })}
        </div>
      </div>
      {issueBundles.length === 0 ? (
        <div style={{ padding: 14, color: 'var(--fg-3)', fontSize: 13 }}>
          지금 선택한 조건의 처리 필요 재료가 없습니다.
        </div>
      ) : (
        <div style={scrollTableWrapStyle}>
          <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-weak)', textAlign: 'left' }}>
                <th style={th}>상태</th>
                <th style={th}>재료</th>
                <th style={th}>근거</th>
                <th style={th}>넘길 곳</th>
                <th style={th}>바로 처리</th>
              </tr>
            </thead>
            <tbody>
              {issueBundles.slice(0, 12).map((bundle) => {
                const issueTitles = uniqueIssueValues(bundle.issues, (issue) => issue.title);
                const routeLabels = uniqueIssueValues(bundle.issues, (issue) => issue.routeLabel);
                return (
                <tr key={bundle.row.id} style={{ borderBottom: '1px solid var(--border-weak)' }}>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                      {issueTitles.map((title) => {
                        const first = bundle.issues.find((issue) => issue.title === title);
                        return (
                          <span
                            key={title}
                            style={{
                              border: '1px solid var(--border-base)',
                              borderRadius: 999,
                              padding: '3px 7px',
                              color: issueSeverityColor(first?.severity ?? 'info'),
                              fontSize: 12,
                              fontWeight: 800,
                              background: 'var(--bg-surface)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {title}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <ItemThumb name={bundle.row.name} imageUrl={bundle.row.imageUrl ?? null} />
                      <div>
                        <div style={{ fontWeight: 700 }}>{bundle.row.name}</div>
                        <div style={{ color: 'var(--fg-3)', fontSize: 12 }}>
                          {bundle.row.locationName ?? '사업장'} · {bundle.row.stock.toLocaleString()}{' '}
                          {bundle.row.unit}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {bundle.issues.slice(0, 3).map((issue) => (
                        <span key={issue.id}>{issue.detail}</span>
                      ))}
                      {bundle.issues.length > 3 && (
                        <span style={{ color: 'var(--fg-3)' }}>
                          {bundle.issues.length - 3}개 이유 더 있음
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                      {routeLabels.map((label) => (
                        <span
                          key={label}
                          style={{
                            border: '1px solid var(--border-base)',
                            borderRadius: 999,
                            padding: '3px 7px',
                            color: 'var(--fg-2)',
                            fontSize: 12,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td style={td}>
                    {isOwner ? (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <Button size="sm" variant="secondary" onClick={() => onReceive(bundle.row)}>
                          입고/처리
                        </Button>
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          {issueBundles.length > 12 && (
            <div style={{ padding: '8px 14px', color: 'var(--fg-3)', fontSize: 12 }}>
              {issueBundles.length - 12}개 재료 더 있음. 필터나 검색으로 좁혀서 확인하세요.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MaterialTable({
  items,
  isOwner,
  quickUpdatePending,
  onQuickUpdate,
  onReceive,
  onEdit,
  onRemove,
}: {
  items: InventoryMaterialRow[];
  isOwner: boolean;
  quickUpdatePending: boolean;
  onQuickUpdate: (id: string, body: Record<string, unknown>) => void;
  onReceive: (row: InventoryMaterialRow) => void;
  onEdit: (row: InventoryMaterialRow) => void;
  onRemove: (row: InventoryMaterialRow) => void;
}) {
  return (
    <div style={scrollTableWrapStyle}>
      <table style={{ width: '100%', minWidth: 1120, borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr
            style={{
              borderBottom: '1px solid var(--border-base)',
              textAlign: 'left',
            }}
          >
            <th style={{ ...th, width: 48 }}></th>
            <th style={th}>재료</th>
            <th style={{ ...th, textAlign: 'right' }}>BoxHero 현재</th>
            <th style={th}>환산</th>
            <th style={{ ...th, textAlign: 'right' }}>비교 기준</th>
            <th style={{ ...th, textAlign: 'right' }}>우리 현재</th>
            <th style={{ ...th, textAlign: 'right' }}>차이</th>
            <th style={{ ...th, textAlign: 'right' }}>안전</th>
            <th style={{ ...th, textAlign: 'right' }}>적정</th>
            <th style={{ ...th, textAlign: 'right' }}>부족</th>
            <th style={th}>유통기한</th>
            <th style={th}>연결</th>
            {isOwner && <th style={th}>작업</th>}
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr style={{ borderBottom: '1px solid var(--border-weak)' }}>
              <td colSpan={isOwner ? 13 : 12} style={{ ...td, padding: 24, color: 'var(--fg-3)' }}>
                등록된 우리 재료가 없습니다.
              </td>
            </tr>
          ) : (
            items.map((m) => (
              <tr key={m.id} style={{ borderBottom: '1px solid var(--border-weak)' }}>
                <td style={td}>
                  <ItemThumb name={m.name} imageUrl={m.imageUrl ?? null} />
                </td>
                <td style={td}>
                  <div style={{ fontWeight: 700 }}>{m.name}</div>
                  <div style={{ color: 'var(--fg-3)', fontSize: 12 }}>{m.unit}</div>
                </td>
                <td style={{ ...td, textAlign: 'right' }}>
                  {m.providerQuantity == null
                    ? '—'
                    : `${m.providerQuantity.toLocaleString()} ${m.providerUnit ?? '외부'}`}
                </td>
                <td style={td}>
                  {m.providerKind === 'boxhero' || hasOrderConversion(m) ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span>
                        1 {m.providerUnit ?? '외부'} = {(m.conversionRatio ?? 1).toLocaleString()}{' '}
                        {m.unit}
                      </span>
                      <span style={{ color: 'var(--fg-3)', fontSize: 12 }}>
                        {m.providerKind === 'boxhero'
                          ? m.convertedProviderQuantity == null
                            ? '외부 수량 없음'
                            : `${m.convertedProviderQuantity.toLocaleString()} ${m.unit}`
                          : '발주 단위'}
                      </span>
                    </div>
                  ) : (
                    '자체'
                  )}
                </td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <div style={{ fontWeight: 700 }}>
                    {formatComparableQuantity(m)}
                  </div>
                  <div style={{ color: 'var(--fg-3)', fontSize: 12 }}>
                    {conversionDetail(m)}
                  </div>
                </td>
                <td style={{ ...td, textAlign: 'right' }}>
                  {m.stock.toLocaleString()} {m.unit}
                </td>
                <td style={{ ...td, textAlign: 'right' }}>
                  {formatDifference(m.stockDifference, m.unit)}
                </td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <QuickNumberInput
                    value={m.minStock}
                    disabled={!isOwner || quickUpdatePending}
                    onCommit={(next) => onQuickUpdate(m.id, { minStock: next })}
                  />
                  <div style={{ color: 'var(--fg-3)', fontSize: 11 }}>{m.comparableUnit}</div>
                </td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <QuickNumberInput
                    value={m.targetStock}
                    disabled={!isOwner || quickUpdatePending}
                    onCommit={(next) => onQuickUpdate(m.id, { targetStock: next })}
                  />
                  <div style={{ color: 'var(--fg-3)', fontSize: 11 }}>{m.comparableUnit}</div>
                </td>
                <td style={{ ...td, textAlign: 'right' }}>
                  {m.shortage > 0 ? (
                    <span style={{ color: 'var(--danger)', fontWeight: 700 }}>
                      {m.shortage.toLocaleString()} {m.comparableUnit}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td style={td}>
                  <QuickDateInput
                    value={m.expiryDate ?? ''}
                    disabled={!isOwner || quickUpdatePending}
                    onCommit={(next) => onQuickUpdate(m.id, { expiryDate: next || null })}
                  />
                </td>
                <td style={td}>
                  {m.providerKind === 'boxhero' ? `BoxHero #${m.providerItemId ?? '-'}` : '직접 등록'}
                </td>
                {isOwner && (
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <Button size="sm" variant="secondary" onClick={() => onReceive(m)}>
                        입고
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => onEdit(m)}>
                        수정
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onRemove(m)}
                        style={{ color: 'var(--danger)' }}
                      >
                        삭제
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function CandidateTable({
  rows,
  isOwner,
  bulkExpiryDate,
  locationLinkText,
  importPending,
  onLink,
}: {
  rows: InventoryItemRow[];
  isOwner: boolean;
  bulkExpiryDate: string;
  locationLinkText: string;
  importPending: boolean;
  onLink: (row: InventoryItemRow) => void;
}) {
  return (
    <div style={scrollTableWrapStyle}>
      <table style={{ width: '100%', minWidth: 860, borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border-base)', textAlign: 'left' }}>
            <th style={{ ...th, width: 48 }}></th>
            <th style={th}>BoxHero 품목</th>
            <th style={{ ...th, textAlign: 'right' }}>BoxHero 현재</th>
            <th style={th}>기본 환산</th>
            <th style={{ ...th, textAlign: 'right' }}>우리 환산</th>
            <th style={th}>유통기한</th>
            <th style={th}>연결</th>
            {isOwner && <th style={th}>작업</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const providerUnit = extractUnit(row.attrs) || '외부';
            return (
              <tr key={row.id} style={{ borderBottom: '1px solid var(--border-weak)' }}>
                <td style={td}>
                  <ItemThumb name={row.name} imageUrl={row.photoUrl} />
                </td>
                <td style={td}>
                  <div style={{ fontWeight: 700 }}>{row.name}</div>
                  <div style={{ color: 'var(--fg-3)', fontSize: 12 }}>
                    {row.sku ? `SKU: ${row.sku}` : 'SKU 없음'}
                  </div>
                </td>
                <td style={{ ...td, textAlign: 'right' }}>
                  {row.quantity.toLocaleString()} {providerUnit}
                </td>
                <td style={td}>1 {providerUnit} = 1 {providerUnit}</td>
                <td style={{ ...td, textAlign: 'right' }}>
                  {row.quantity.toLocaleString()} {providerUnit}
                </td>
                <td style={td}>{bulkExpiryDate || '선택'}</td>
                <td style={td}>{locationLinkText}</td>
                {isOwner && (
                  <td style={td}>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => onLink(row)}
                      disabled={importPending}
                    >
                      개별 연동
                    </Button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function formatDifference(value: number | null, unit: string): string {
  if (value == null) return '—';
  if (Math.abs(value) < 0.0001) return '0';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toLocaleString()} ${unit}`;
}

function QuickNumberInput({
  value,
  disabled,
  onCommit,
}: {
  value: number;
  disabled: boolean;
  onCommit: (value: number) => void;
}) {
  const [draft, setDraft] = useState(String(value));

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = () => {
    const next = Number(draft);
    if (!Number.isFinite(next) || next < 0 || next === value) return;
    onCommit(next);
  };

  return (
    <input
      type="number"
      min="0"
      value={draft}
      disabled={disabled}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.currentTarget.blur();
        }
      }}
      style={{
        width: 72,
        minHeight: 'var(--control-min-h)',
        boxSizing: 'border-box',
        padding: '5px 7px',
        border: '1px solid var(--border-base)',
        borderRadius: 6,
        fontSize: 12,
        textAlign: 'right',
        background: disabled ? 'var(--bg-subtle)' : 'var(--bg-surface)',
        color: 'var(--fg-1)',
      }}
    />
  );
}

function QuickDateInput({
  value,
  disabled,
  onCommit,
}: {
  value: string;
  disabled: boolean;
  onCommit: (value: string) => void;
}) {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const commit = () => {
    if (draft === value) return;
    onCommit(draft);
  };

  return (
    <input
      type="date"
      value={draft}
      disabled={disabled}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      style={{
        width: 132,
        minHeight: 'var(--control-min-h)',
        boxSizing: 'border-box',
        padding: '5px 7px',
        border: '1px solid var(--border-base)',
        borderRadius: 6,
        fontSize: 12,
        background: disabled ? 'var(--bg-subtle)' : 'var(--bg-surface)',
        color: 'var(--fg-1)',
      }}
    />
  );
}

function BoxheroMappingModal({
  mappings,
  boxheroLocations,
  selectedLocationId,
  onClose,
  onChanged,
}: {
  mappings: LocationMappingRow[];
  boxheroLocations: Array<{ id: string; boxheroLocationId: string; name: string }>;
  selectedLocationId: string | null;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const toast = useToast();
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  useEffect(() => {
    setDrafts(Object.fromEntries(mappings.map((row) => [row.id, row.boxheroLocationId ?? ''])));
  }, [mappings]);

  const updateMu = useMutation({
    mutationFn: async ({ id, next }: { id: string; next: string | null }) => {
      const r = await locationsControllerUpdate({
        path: { id },
        body: { boxheroLocationId: next ? Number(next) : null } as never,
      });
      return unwrap(r);
    },
    onSuccess: async () => {
      await onChanged();
      toast.success('BoxHero 위치 연동을 저장했습니다.');
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? 'BoxHero 위치 연동 저장 실패');
    },
  });

  const nameByBoxheroId = new Map(boxheroLocations.map((loc) => [loc.boxheroLocationId, loc.name]));

  const confirmChange = (row: LocationMappingRow, next: string | null) => {
    const currentName = row.boxheroLocationId
      ? nameByBoxheroId.get(row.boxheroLocationId) ?? `ID ${row.boxheroLocationId}`
      : '미연동';
    const nextName = next ? nameByBoxheroId.get(next) ?? `ID ${next}` : '연결 해제';
    const usedBy = next
      ? mappings.find((m) => m.id !== row.id && m.boxheroLocationId === next)
      : null;
    if (!row.boxheroLocationId && row.boxheroMaterialCount === 0 && !usedBy) return true;

    const warning = [
      `${row.name}의 BoxHero 위치를 바꿉니다.`,
      `현재: ${currentName}`,
      `변경: ${nextName}`,
      row.boxheroMaterialCount > 0
        ? `이미 BoxHero 연동 재료 ${row.boxheroMaterialCount}개가 있습니다. 기존 재료는 삭제되지 않지만 앞으로 후보/연동 기준 위치가 바뀝니다.`
        : '기존 재료는 삭제되지 않지만 앞으로 후보/연동 기준 위치가 바뀝니다.',
      usedBy ? `주의: ${nextName}은 이미 ${usedBy.name}에 연결되어 있습니다.` : '',
      '계속할까요?',
    ]
      .filter(Boolean)
      .join('\n');
    return window.confirm(warning);
  };

  const saveMapping = (row: LocationMappingRow) => {
    const next = drafts[row.id] ?? '';
    if (!next || next === (row.boxheroLocationId ?? '')) return;
    if (!confirmChange(row, next)) return;
    updateMu.mutate({ id: row.id, next });
  };

  const unlinkMapping = (row: LocationMappingRow) => {
    if (!row.boxheroLocationId) return;
    if (!confirmChange(row, null)) return;
    updateMu.mutate({ id: row.id, next: null });
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="BoxHero 외부 재고 연동"
      size="lg"
      footer={
        <Button variant="secondary" onClick={onClose} disabled={updateMu.isPending}>
          닫기
        </Button>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {boxheroLocations.length === 0 ? (
          <AlertBanner
            tone="warning"
            message="BoxHero 위치 목록을 불러오지 못했습니다. BoxHero 토큰 상태를 확인하세요."
          />
        ) : null}
        <div style={scrollTableWrapStyle}>
          <table style={{ width: '100%', minWidth: 680, borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-base)', textAlign: 'left' }}>
                <th style={th}>우리 사업장</th>
                <th style={th}>BoxHero 위치</th>
                <th style={{ ...th, textAlign: 'right' }}>연동 재료</th>
                <th style={th}>작업</th>
              </tr>
            </thead>
            <tbody>
              {mappings.map((row) => {
                const draft = drafts[row.id] ?? '';
                const currentMissing =
                  row.boxheroLocationId != null && !nameByBoxheroId.has(row.boxheroLocationId);
                const changed = draft !== (row.boxheroLocationId ?? '');
                const highlighted = row.id === selectedLocationId;
                return (
                  <tr
                    key={row.id}
                    style={{
                      borderBottom: '1px solid var(--border-weak)',
                      background: highlighted ? 'var(--bg-subtle)' : 'transparent',
                    }}
                  >
                    <td style={td}>{row.name}</td>
                    <td style={td}>
                      <select
                        value={draft}
                        onChange={(e) =>
                          setDrafts((prev) => ({ ...prev, [row.id]: e.target.value }))
                        }
                        disabled={updateMu.isPending || boxheroLocations.length === 0}
                        style={{
                          width: '100%',
                          minWidth: 220,
                          minHeight: 'var(--control-min-h)',
                          boxSizing: 'border-box',
                          padding: '7px 10px',
                          border: '1px solid var(--border-base)',
                          borderRadius: 6,
                          fontSize: 13,
                          background: 'var(--bg-surface)',
                          color: 'var(--fg-1)',
                        }}
                      >
                        <option value="">미연동</option>
                        {currentMissing && (
                          <option value={row.boxheroLocationId ?? ''}>
                            현재 ID {row.boxheroLocationId}
                          </option>
                        )}
                        {boxheroLocations.map((loc) => {
                          const usedBy = mappings.find(
                            (m) => m.id !== row.id && m.boxheroLocationId === loc.boxheroLocationId,
                          );
                          return (
                            <option key={loc.id} value={loc.boxheroLocationId}>
                              {usedBy ? `${loc.name} · ${usedBy.name} 연결됨` : loc.name}
                            </option>
                          );
                        })}
                      </select>
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {row.boxheroMaterialCount.toLocaleString()}개
                    </td>
                    <td style={td}>
                      <Button
                        size="sm"
                        variant="strong"
                        onClick={() => saveMapping(row)}
                        disabled={!draft || !changed || updateMu.isPending}
                      >
                        {row.boxheroLocationId ? '변경' : '연결'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => unlinkMapping(row)}
                        disabled={!row.boxheroLocationId || updateMu.isPending}
                        style={{ marginLeft: 4, color: 'var(--danger)' }}
                      >
                        연결 해제
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
}

async function uploadMaterialImage(file: File): Promise<string> {
  const token = getStoredToken();
  const body = new FormData();
  body.append('file', file);
  const res = await fetch(`${API_BASE}/v1/inventory/materials/image`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body,
  });
  const json = (await res.json().catch(() => null)) as
    | { data?: { imageUrl?: string }; message?: string }
    | null;
  if (!res.ok || !json?.data?.imageUrl) {
    throw new ApiCallError(json?.message ?? '이미지 업로드 실패', { status: res.status });
  }
  return json.data.imageUrl;
}

function UnitSelect({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      style={{
        width: '100%',
        minWidth: 0,
        minHeight: 'var(--control-min-h)',
        boxSizing: 'border-box',
        padding: '7px 10px',
        border: '1px solid var(--border-base)',
        borderRadius: 6,
        fontSize: 13,
        background: disabled ? 'var(--bg-subtle)' : 'var(--bg-surface)',
        color: 'var(--fg-1)',
      }}
    >
      <option value="g">g</option>
      <option value="ml">ml</option>
      <option value="ea">ea</option>
    </select>
  );
}

type StockInputBasis = 'order' | 'operating';

function formatInputNumber(value: number): string {
  if (!Number.isFinite(value)) return '';
  return String(Math.round(value * 10000) / 10000);
}

function parseOptionalQuantity(value: string): number | null {
  if (value.trim() === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function materialStockFromInput(
  value: string,
  basis: StockInputBasis,
  ratio: number,
): number | null {
  const qty = parseOptionalQuantity(value);
  if (qty == null) return null;
  if (basis === 'order' && (!Number.isFinite(ratio) || ratio <= 0)) return null;
  return basis === 'order' ? qty * ratio : qty;
}

function stockBasisUnit(
  basis: StockInputBasis,
  orderUnit: string,
  operatingUnit: string,
): string {
  return basis === 'order' ? orderUnit.trim() || '발주 단위' : operatingUnit.trim() || '우리 단위';
}

function StockBasisToggle({
  value,
  onChange,
  orderUnit,
  operatingUnit,
  disabled,
}: {
  value: StockInputBasis;
  onChange: (value: StockInputBasis) => void;
  orderUnit: string;
  operatingUnit: string;
  disabled?: boolean;
}) {
  const options: Array<{ value: StockInputBasis; label: string; unit: string }> = [
    { value: 'order', label: '발주 단위', unit: orderUnit.trim() || '발주 단위' },
    { value: 'operating', label: '우리 단위', unit: operatingUnit.trim() || '우리 단위' },
  ];
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {options.map((option) => (
        <Button
          key={option.value}
          type="button"
          size="sm"
          variant={value === option.value ? 'strong' : 'secondary'}
          onClick={() => onChange(option.value)}
          disabled={disabled}
        >
          {option.label} ({option.unit})
        </Button>
      ))}
    </div>
  );
}

function LinkBoxheroMaterialModal({
  row,
  location,
  isPending,
  onClose,
  onSubmit,
}: {
  row: InventoryItemRow;
  location: LocationRow | null;
  isPending: boolean;
  onClose: () => void;
  onSubmit: (draft: LinkMaterialDraft) => void;
}) {
  const providerUnitDefault = extractUnit(row.attrs) || 'ea';
  const initialUnit = OPERATING_UNITS.includes(providerUnitDefault as (typeof OPERATING_UNITS)[number])
    ? providerUnitDefault
    : 'ea';
  const [unit, setUnit] = useState(initialUnit);
  const [providerUnit, setProviderUnit] = useState(providerUnitDefault);
  const [conversionRatio, setConversionRatio] = useState('1');
  const [minStock, setMinStock] = useState('');
  const [targetStock, setTargetStock] = useState('');
  const [expiryDate, setExpiryDate] = useState('');

  const ratio = Number(conversionRatio);
  const canSubmit =
    !!location &&
    OPERATING_UNITS.includes(unit as (typeof OPERATING_UNITS)[number]) &&
    !!providerUnit.trim() &&
    Number.isFinite(ratio) &&
    ratio > 0 &&
    !isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title={`BoxHero 재료 연동 — ${row.name}`}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isPending}>
            취소
          </Button>
          <Button
            variant="strong"
            onClick={() =>
              onSubmit({
                row,
                unit: unit.trim(),
                providerUnit: providerUnit.trim(),
                conversionRatio: ratio,
                minStock: minStock ? Number(minStock) : undefined,
                targetStock: targetStock ? Number(targetStock) : undefined,
                expiryDate: expiryDate || undefined,
              })
            }
            disabled={!canSubmit}
          >
            {isPending ? '연동 중…' : '연동'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '40px minmax(0, 1fr)',
            gap: 10,
            alignItems: 'center',
          }}
        >
          <ItemThumb name={row.name} imageUrl={row.photoUrl} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>{row.name}</div>
            <div style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 2 }}>
              BoxHero 수량 {row.quantity.toLocaleString()} {providerUnitDefault}
            </div>
          </div>
        </div>
        <FormField label="우리 사업장" required>
          <TextInput value={location?.name ?? ''} onChange={() => undefined} disabled />
        </FormField>
        <FormField label="우리 재료 단위" required>
          <UnitSelect value={unit} onChange={setUnit} />
        </FormField>
        <FormField label="BoxHero 외부 단위" required>
          <TextInput value={providerUnit} onChange={setProviderUnit} placeholder="예: box, kg, ea" />
        </FormField>
        <FormField label="환산비" required>
          <TextInput
            type="number"
            value={conversionRatio}
            onChange={setConversionRatio}
            placeholder="외부 1단위가 우리 재료 몇 단위인지"
          />
        </FormField>
        <FormField label="유통기한">
          <TextInput type="date" value={expiryDate} onChange={setExpiryDate} />
        </FormField>
        <FormField label="최소 재고">
          <TextInput type="number" value={minStock} onChange={setMinStock} placeholder="(선택)" />
        </FormField>
        <FormField label="목표 재고">
          <TextInput
            type="number"
            value={targetStock}
            onChange={setTargetStock}
            placeholder="(선택)"
          />
        </FormField>
      </div>
    </Modal>
  );
}

function ReceiveMaterialModal({
  row,
  isPending,
  onClose,
  onSubmit,
}: {
  row: InventoryMaterialRow;
  isPending: boolean;
  onClose: () => void;
  onSubmit: (qty: number, note?: string, expiryDate?: string) => void;
}) {
  const [qty, setQty] = useState('');
  const canUseOrderBasis = row.providerKind !== 'boxhero' && hasOrderConversion(row);
  const [qtyBasis, setQtyBasis] = useState<StockInputBasis>(
    canUseOrderBasis ? 'order' : 'operating',
  );
  const [expiryDate, setExpiryDate] = useState(row.expiryDate ?? '');
  const [note, setNote] = useState('');
  const qtyNumber = Number(qty);
  const ratio = row.conversionRatio ?? 1;
  const adjustedQty =
    canUseOrderBasis && qtyBasis === 'order' && Number.isFinite(qtyNumber)
      ? qtyNumber * ratio
      : qtyNumber;
  const expiryManaged = !!row.expiryDate;
  const canSubmit =
    Number.isFinite(adjustedQty) &&
    adjustedQty > 0 &&
    (!expiryManaged || !!expiryDate) &&
    !isPending;
  const quantityUnit = canUseOrderBasis
    ? stockBasisUnit(qtyBasis, row.providerUnit ?? '', row.unit)
    : row.unit;
  const convertedPreview =
    canUseOrderBasis && qtyBasis === 'order' && Number.isFinite(adjustedQty) && adjustedQty > 0
      ? `${formatQuantity(adjustedQty)} ${row.unit}`
      : null;
  const finalNote =
    canUseOrderBasis && qtyBasis === 'order'
      ? [
          note.trim(),
          `${formatQuantity(qtyNumber)} ${row.providerUnit ?? '발주 단위'} 입고 = ${formatQuantity(adjustedQty)} ${row.unit}`,
        ]
          .filter(Boolean)
          .join(' · ')
      : note.trim() || undefined;

  return (
    <Modal
      open
      onClose={onClose}
      title={`입고/처리 — ${row.name}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isPending}>
            취소
          </Button>
          <Button
            variant="strong"
            onClick={() =>
              onSubmit(adjustedQty, finalNote, expiryDate || undefined)
            }
            disabled={!canSubmit}
          >
            {isPending ? '반영 중…' : '입고 반영'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 13, color: 'var(--fg-2)' }}>
          현재 {row.stock.toLocaleString()} {row.unit}
        </div>
        {canUseOrderBasis && (
          <FormField label="입고 기준" required>
            <StockBasisToggle
              value={qtyBasis}
              onChange={setQtyBasis}
              orderUnit={row.providerUnit ?? ''}
              operatingUnit={row.unit}
              disabled={isPending}
            />
          </FormField>
        )}
        <FormField label={`입고 수량 (${quantityUnit})`} required>
          <TextInput type="number" value={qty} onChange={setQty} placeholder="0" />
          {convertedPreview && (
            <div style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 4 }}>
              우리 현재 +{convertedPreview}
            </div>
          )}
        </FormField>
        <FormField label={expiryManaged ? '유통기한' : '유통기한 (선택)' } required={expiryManaged}>
          <TextInput type="date" value={expiryDate} onChange={setExpiryDate} />
        </FormField>
        <FormField label="메모">
          <TextInput value={note} onChange={setNote} placeholder="예: 실입고, 재고조정, 유통기한 교체" />
        </FormField>
      </div>
    </Modal>
  );
}

function CreateMaterialModal({
  location,
  onClose,
  onCreated,
}: {
  location: LocationRow | null;
  onClose: () => void;
  onCreated: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [unit, setUnit] = useState('ea');
  const [orderUnit, setOrderUnit] = useState('');
  const [cost, setCost] = useState('');
  const [minStock, setMinStock] = useState('');
  const [targetStock, setTargetStock] = useState('');
  const [stock, setStock] = useState('');
  const [stockBasis, setStockBasis] = useState<StockInputBasis>('order');
  const [conversionRatio, setConversionRatio] = useState('1');
  const [expiryDate, setExpiryDate] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);

  const ratio = Number(conversionRatio);
  const openingStock = materialStockFromInput(stock, stockBasis, ratio);
  const stockInputValid = stock.trim() === '' || openingStock != null;
  const stockUnit = stockBasisUnit(stockBasis, orderUnit, unit);

  const m = useMutation({
    mutationFn: async () => {
      if (!Number.isFinite(ratio) || ratio <= 0) {
        throw new Error('환산비는 0보다 큰 숫자로 입력하세요.');
      }
      if (!stockInputValid) {
        throw new Error('현재 재고는 0 이상 숫자로 입력하세요.');
      }
      const imageUrl = imageFile ? await uploadMaterialImage(imageFile) : undefined;
      const body: Record<string, unknown> = {
        locationId: location?.id,
        name: name.trim(),
        unit: unit.trim(),
        providerUnit: orderUnit.trim(),
        conversionRatio: ratio,
      };
      if (cost) body.cost = Number(cost);
      if (minStock) body.minStock = Number(minStock);
      if (targetStock) body.targetStock = Number(targetStock);
      if (openingStock != null) body.stock = openingStock;
      if (expiryDate) body.expiryDate = expiryDate;
      if (imageUrl) body.imageUrl = imageUrl;
      const r = await inventoryControllerCreateMaterial({ body: body as never });
      return unwrap(r);
    },
    onSuccess: () => {
      toast.success('자체 재료를 등록했습니다.');
      onCreated();
    },
    onError: (e) => {
      const err = e as ApiCallError;
      if (err.code === 'CONFLICT') {
        toast.error('같은 이름의 재료가 이미 있습니다.');
      } else {
        toast.error(err.message ?? '등록 실패');
      }
    },
  });

  const canSubmit =
    !!location &&
    !!name.trim() &&
    !!unit.trim() &&
    !!orderUnit.trim() &&
    Number.isFinite(ratio) &&
    ratio > 0 &&
    stockInputValid &&
    !m.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title="자체 재료 등록"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={m.isPending}>
            취소
          </Button>
          <Button
            variant="strong"
            onClick={() => m.mutate()}
            disabled={!canSubmit}
          >
            {m.isPending ? '등록 중…' : '자체 등록'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <FormField label="이름" required>
          <TextInput value={name} onChange={setName} placeholder="예: 양파" />
        </FormField>
        <FormField label="사업장" required>
          <TextInput value={location?.name ?? ''} onChange={() => undefined} disabled />
        </FormField>
        <FormField label="이미지">
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            onChange={(e) => setImageFile(e.target.files?.[0] ?? null)}
            style={{ fontSize: 13 }}
          />
        </FormField>
        <FormField label="우리 재료 단위" required>
          <TextInput value={unit} onChange={setUnit} placeholder="예: ea, g, ml, 장, 줄" />
        </FormField>
        <FormField label="발주 단위" required>
          <TextInput value={orderUnit} onChange={setOrderUnit} placeholder="예: 박스, 묶음, 팩" />
        </FormField>
        <FormField label="단가 (원)">
          <TextInput
            type="number"
            value={cost}
            onChange={setCost}
            placeholder="(선택)"
          />
        </FormField>
        <FormField label="최소 재고">
          <TextInput
            type="number"
            value={minStock}
            onChange={setMinStock}
            placeholder="(선택)"
          />
        </FormField>
        <FormField label="목표 재고">
          <TextInput
            type="number"
            value={targetStock}
            onChange={setTargetStock}
            placeholder="(선택)"
          />
        </FormField>
        <FormField label="현재 재고">
          <StockBasisToggle
            value={stockBasis}
            onChange={setStockBasis}
            orderUnit={orderUnit}
            operatingUnit={unit}
            disabled={m.isPending}
          />
          <TextInput
            type="number"
            value={stock}
            onChange={setStock}
            placeholder={`(${stockUnit})`}
          />
          {openingStock != null && stockBasis === 'order' && (
            <div style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 4 }}>
              우리 현재 {formatQuantity(openingStock)} {unit.trim() || '우리 단위'}
            </div>
          )}
        </FormField>
        <FormField label="환산비" required>
          <TextInput
            type="number"
            value={conversionRatio}
            onChange={setConversionRatio}
            placeholder="발주 1단위가 우리 재료 몇 단위인지"
          />
        </FormField>
        <FormField label="유통기한">
          <TextInput type="date" value={expiryDate} onChange={setExpiryDate} />
        </FormField>
      </div>
    </Modal>
  );
}

function EditMaterialModal({
  row,
  onClose,
  onSaved,
}: {
  row: InventoryMaterialRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState(row.name);
  const [unit, setUnit] = useState(row.unit);
  const [cost, setCost] = useState(String(row.cost));
  const [minStock, setMinStock] = useState(String(row.minStock));
  const [targetStock, setTargetStock] = useState(String(row.targetStock));
  const [stock, setStock] = useState(String(row.stock));
  const canUseOrderBasis = row.providerKind !== 'boxhero';
  const [stockBasis, setStockBasis] = useState<StockInputBasis>('operating');
  const [providerUnit, setProviderUnit] = useState(row.providerUnit ?? '');
  const [conversionRatio, setConversionRatio] = useState(String(row.conversionRatio ?? 1));
  const [expiryDate, setExpiryDate] = useState(row.expiryDate ?? '');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const ratio = Number(conversionRatio);
  const nextStock = materialStockFromInput(stock, stockBasis, ratio);
  const stockUnit = stockBasisUnit(stockBasis, providerUnit, unit);

  const handleStockBasisChange = (nextBasis: StockInputBasis) => {
    if (nextBasis === stockBasis) return;
    const typed = Number(stock);
    if (Number.isFinite(typed) && typed >= 0 && Number.isFinite(ratio) && ratio > 0) {
      const operatingStock = stockBasis === 'order' ? typed * ratio : typed;
      setStock(formatInputNumber(nextBasis === 'order' ? operatingStock / ratio : operatingStock));
    }
    setStockBasis(nextBasis);
  };

  const m = useMutation({
    mutationFn: async () => {
      if (nextStock == null) {
        throw new Error('현재 재고는 0 이상 숫자로 입력하세요.');
      }
      if (!providerUnit.trim()) {
        throw new Error('발주 단위를 입력하세요.');
      }
      if (!Number.isFinite(ratio) || ratio <= 0) {
        throw new Error('환산비는 0보다 큰 숫자로 입력하세요.');
      }
      const stockDelta = nextStock - row.stock;
      const imageUrl = imageFile ? await uploadMaterialImage(imageFile) : undefined;
      const body: Record<string, unknown> = {
        name: name.trim(),
        unit: unit.trim(),
        cost: Number(cost) || 0,
        minStock: Number(minStock) || 0,
        targetStock: Number(targetStock) || 0,
        providerUnit: providerUnit.trim(),
        conversionRatio: ratio,
        expiryDate: expiryDate || null,
      };
      if (imageUrl) body.imageUrl = imageUrl;
      const r = await inventoryControllerUpdateMaterial({
        path: { id: row.id },
        body: body as never,
      });
      const updated = unwrap(r);
      if (Math.abs(stockDelta) < 0.0001) return updated;
      const adjusted = await inventoryControllerAdjustMaterialStock({
        path: { id: row.id },
        body: {
          qty: stockDelta,
          reason: stockDelta > 0 ? 'manual_in' : 'manual_adjust',
          note: `재료 수정에서 현재 재고 보정 (${row.stock} → ${nextStock} ${row.unit})`,
        } as never,
      });
      return unwrap(adjusted);
    },
    onSuccess: () => {
      toast.success('수정했습니다.');
      onSaved();
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '수정 실패');
    },
  });
  const canSubmit =
    !!name.trim() &&
    !!unit.trim() &&
    !!providerUnit.trim() &&
    Number.isFinite(ratio) &&
    ratio > 0 &&
    nextStock != null &&
    !m.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title={`재료 수정 — ${row.name}`}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={m.isPending}>
            취소
          </Button>
          <Button
            variant="strong"
            onClick={() => m.mutate()}
            disabled={!canSubmit}
          >
            {m.isPending ? '저장 중…' : '저장'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <FormField label="이름" required>
          <TextInput value={name} onChange={setName} />
        </FormField>
        <FormField label="사업장" required>
          <TextInput value={row.locationName ?? ''} onChange={() => undefined} disabled />
        </FormField>
        <FormField label="우리 재료 단위" required>
          <TextInput value={unit} onChange={setUnit} placeholder="예: ea, g, ml, 장, 줄" />
        </FormField>
        <FormField label="이미지">
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            onChange={(e) => setImageFile(e.target.files?.[0] ?? null)}
            style={{ fontSize: 13 }}
          />
        </FormField>
        <FormField label="단가 (원)">
          <TextInput type="number" value={cost} onChange={setCost} />
        </FormField>
        <FormField label="최소 재고">
          <TextInput type="number" value={minStock} onChange={setMinStock} />
        </FormField>
        <FormField label="목표 재고">
          <TextInput type="number" value={targetStock} onChange={setTargetStock} />
        </FormField>
        <FormField label="현재 재고">
          {canUseOrderBasis && (
            <StockBasisToggle
              value={stockBasis}
              onChange={handleStockBasisChange}
              orderUnit={providerUnit}
              operatingUnit={unit}
              disabled={m.isPending}
            />
          )}
          <TextInput
            type="number"
            value={stock}
            onChange={setStock}
            placeholder={`현재 ${row.stock.toLocaleString()} ${row.unit} · 입력 ${stockUnit}`}
          />
          {nextStock != null && stockBasis === 'order' && (
            <div style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 4 }}>
              우리 현재 {formatQuantity(nextStock)} {unit.trim() || '우리 단위'}
            </div>
          )}
        </FormField>
        <FormField label="발주 단위" required>
          <TextInput value={providerUnit} onChange={setProviderUnit} placeholder="예: 박스, 묶음, 팩" />
        </FormField>
        <FormField label="환산비" required>
          <TextInput
            type="number"
            value={conversionRatio}
            onChange={setConversionRatio}
            placeholder="발주 1단위가 우리 재료 몇 단위인지"
          />
        </FormField>
        <FormField label="유통기한">
          <TextInput type="date" value={expiryDate} onChange={setExpiryDate} />
        </FormField>
      </div>
    </Modal>
  );
}
